import { Link } from 'react-router-dom';
import { Truck, Check, Clock, Shield, ArrowRight, Phone, Zap, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const TruckFinance = () => {
  const features = [
    'New and used trucks accepted',
    'Prime movers, rigids, and trailers',
    'Flexible terms from 12-84 months',
    'No deposit options available',
    'Competitive fixed and variable rates',
    'Fleet finance packages',
    'Same-day approvals possible, depending on the applicant and loan type',
    'Australia-wide service'
  ];

  const industries = [
    'Transport & Logistics',
    'Freight & Haulage',
    'Construction',
    'Mining Services',
    'Agriculture',
    'Waste Management',
    'Food Distribution',
    'Moving Services'
  ];

  const faqs = [
    {
      q: "How fast can I get approved for truck finance?",
      a: "Many truck finance applications are approved within 24 hours, and straightforward applications with complete documentation can be approved same day. Timing depends on the applicant, the lender's credit criteria and the type of loan. We understand trucks are essential to your business operations."
    },
    {
      q: "What deposit do I need for truck finance?",
      a: "Deposit requirements vary based on the truck value and your business profile. In many cases, we can offer 100% finance with no deposit required. A deposit of 10-20% may secure better rates."
    },
    {
      q: "Can I finance a used truck?",
      a: "Yes! We finance both new and used trucks. For used trucks, we typically consider vehicles up to 15 years old, depending on condition and your business profile."
    },
    {
      q: "What truck brands do you finance?",
      a: "We finance all major truck brands including Kenworth, Mack, Volvo, Scania, Mercedes-Benz, Isuzu, Hino, Fuso, and more. Any commercial truck can be financed."
    },
    {
      q: "Is truck finance tax deductible?",
      a: "Yes, truck finance repayments are generally tax deductible as a business expense. We also offer chattel mortgage options that may allow you to claim GST on the purchase price. Consult your accountant for specific advice."
    },
    {
      q: "Can I get truck finance with bad credit?",
      a: "We work with a panel of over 40 lenders, including those who specialise in non-conforming lending. While good credit helps, we can often find solutions for businesses with less-than-perfect credit histories."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Truck Finance Australia | Apply for Truck Loans Today"
        description="Apply for truck finance in Australia. Approvals possible within 24 hours for prime movers, rigid trucks & trailers. Loans from $20,000 to $2M+. No deposit options. Call 1800 003 527."
        keywords="truck finance Australia, truck loans, prime mover finance, semi truck finance, commercial truck loans, truck finance broker, rigid truck finance, trailer finance, fleet truck finance"
        canonical="/truck-finance-australia"
      />

      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1920&q=80)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-6">
                <Truck size={18} className="text-gold-500" />
                <span className="text-gold-500 font-body text-sm font-medium">Commercial Truck Finance Specialists</span>
              </div>
              
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-none mb-6">
                Truck Finance<br />
                <span className="gold-gradient-text">Australia</span>
              </h1>
              
              <p className="font-body text-lg text-white/70 mb-8 max-w-xl">
                Get your trucks on the road faster with competitive finance from Australia's trusted commercial vehicle finance broker. Fast approvals, flexible terms, and rates that work for your business.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href={PORTAL_LINKS.COMMERCIAL} data-testid="truck-finance-hero-primary-link">
                  <Button 
                    data-testid="truck-finance-apply-btn"
                    className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-heading font-bold text-lg uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform shadow-lg shadow-purple-500/30"
                  >
                    <Sparkles size={20} className="mr-2" />
                    Get Free Quote
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </a>
                <a href="tel:+611800003527">
                  <Button 
                    variant="outline"
                    className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                  >
                    <Phone size={20} className="mr-2" />
                    1800 003 527
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6 text-white/60">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-gold-500" />
                  <span className="font-body text-sm">24hr Approvals</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-gold-500" />
                  <span className="font-body text-sm">Australian Owned</span>
                </div>
              </div>
            </div>

            <div className="bg-navy-800/80 backdrop-blur border border-white/10 p-8">
              <h3 className="font-heading font-bold text-2xl text-white uppercase mb-6">Quick Quote</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Amounts</span>
                  <span className="font-mono text-xl text-gold-500">$20K - $2M+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Terms</span>
                  <span className="font-mono text-xl text-white">12 - 84 months</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Approval Time</span>
                  <span className="font-mono text-xl text-gold-500 text-right">From 24 hours<span className="block font-body text-[10px] text-white/50">Depends on applicant, credit criteria &amp; loan type</span></span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-white/60">Deposit</span>
                  <span className="font-mono text-xl text-white">From $0</span>
                </div>
              </div>
              <Link to="/calculator" className="block mt-6">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                  Calculate Repayments
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Truck Finance */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
                What is Truck Finance?
              </h2>
              <div className="space-y-4 font-body text-white/70">
                <p>
                  Truck finance is a specialised form of commercial lending designed to help Australian businesses acquire trucks and heavy vehicles. Instead of paying the full purchase price upfront, you can spread the cost over time with manageable monthly repayments.
                </p>
                <p>
                  As an experienced <strong className="text-white">commercial vehicle loan broker</strong>, Flash Funding works with over 40 lenders to find you the best truck finance deal. Whether you're buying your first truck or expanding a fleet, we have solutions to match your needs.
                </p>
                <p>
                  Our truck finance options include chattel mortgage, finance lease, operating lease, and commercial hire purchase — each with different tax and ownership benefits.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 bg-navy-700 p-4">
                  <Check size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                  <span className="font-body text-white/80 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4">
              Industries We Serve
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto">
              Our truck finance solutions support businesses across Australia in these key industries
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div key={index} className="bg-navy-800 border border-white/5 p-6 text-center hover:border-gold-500/30 transition-colors">
                <span className="font-heading font-bold text-white uppercase">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-4">
              <HelpCircle size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">Common Questions</span>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight">
              Truck Finance FAQ
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="bg-navy-700 border border-white/5 px-6"
              >
                <AccordionTrigger className="text-left font-heading font-bold text-lg text-white hover:text-gold-500 py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-white/70 pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gold-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-black uppercase tracking-tight mb-6">
            Ready to Apply for Truck Finance?
          </h2>
          <p className="font-body text-black/70 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote in minutes. Our Australian team is ready to help you get the truck finance you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="truck-finance-final-primary-link">
              <Button 
                className="bg-navy-900 text-white hover:bg-navy-800 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                data-testid="truck-finance-final-primary-cta"
              >
                <Sparkles size={20} className="mr-2" />
                Get Free Quote
              </Button>
            </a>
            <a href="tel:+611800003527">
              <Button 
                variant="outline"
                className="border-black text-black hover:bg-black hover:text-gold-500 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
              >
                Call 1800 003 527
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TruckFinance;
